import { useCallback, useEffect, useState } from "react";
import { EventOrBundle } from "~/lib/definitions";

type Props = {
  eventOrBundle: EventOrBundle;
  disabled?: boolean;
};

type EventOverview = {
  [eventId: string]: {
    [sectionId: string]: number;
  };
};

function parseData(eventData: any): EventOverview[string] {
  try {
    const data = JSON.parse(eventData) as any;

    if (typeof data.event !== "string") {
      throw new Error("Invalid event data");
    }

    if (typeof data.availability !== "object") {
      throw new Error("Invalid availability data");
    }

    const res: EventOverview[string] = {};

    for (const key in data.availability) {
      if (typeof data.availability[key] !== "number") {
        continue;
      }

      res[key] = data.availability[key];
    }

    return res;
  } catch {
    return {};
  }
}

export function useSectionUpdates(props: Props): EventOverview {
  const [overview, setOverview] = useState<EventOverview>({});

  const setupEventUpdates = useCallback((eventId: string): EventSource => {
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_GATEWAY}/booking_gateway.v1.Updates/event/${eventId}`,
    );

    eventSource.addEventListener("EventOverview", (event) => {
      const data = parseData(event.data);

      setOverview((oldOverview) => ({
        ...oldOverview,
        [eventId]: data,
      }));
    });

    eventSource.addEventListener("EventUpdate", (event) => {
      const data = parseData(event.data);

      setOverview((oldOverview) => ({
        ...oldOverview,
        [eventId]: {
          ...oldOverview[eventId],
          ...data,
        },
      }));
    });

    return eventSource;
  }, []);

  const setupBundleUpdates = useCallback(
    (eventIds: string[]): EventSource[] => {
      return eventIds.map((eventId) => setupEventUpdates(eventId));
    },
    [setupEventUpdates],
  );

  useEffect(() => {
    // if (props.disabled) {
    //   setOverview({});
    //   return;
    // }

    const eventSources =
      props.eventOrBundle.case === "event"
        ? [setupEventUpdates(props.eventOrBundle.value.id)]
        : setupBundleUpdates(props.eventOrBundle.value.eventIds);

    return () => {
      for (const eventSource of eventSources) {
        eventSource.close();
      }
    };
  }, [
    props.disabled,
    props.eventOrBundle,
    setupEventUpdates,
    setupBundleUpdates,
  ]);

  return overview;
}
